import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * PageTabs — the one tab set for a page whose tabs switch the rest of the page. They live in
 * PageHeader's right cluster, before the actions (Antonin, 13 Oct, both apps: "tabs with underline,
 * underline touches bottom of header … everywhere in all headers"):
 *   - text 13/20 medium, 24 apart, each tab as tall as the header;
 *   - the active tab in body ink with a 2px accent underline lying on the header's bottom hairline,
 *     the others secondary ink (body ink on hover);
 *   - the label sits on the subline's bottom line, `--page-header-pad` above the hairline;
 *   - an optional count after the label (red only when it asks for action);
 *   - never wraps: what does not fit goes into More (the active tab always stays out), see HeaderTabsFit.
 */
export interface PageTab<Id extends string = string> {
  id: Id;
  label: ReactNode;
  count?: number;
  /** The count asks for action (e.g. overdue requests): its text turns red. */
  urgent?: boolean;
  disabled?: boolean;
}

/** The tab row and tab look, exported for routed tabs (links) that must look exactly like PageTabs.
 *  `page-tab` / `page-tab-on` mark the tabs for HeaderTabsFit.
 *  A second level of tabs DRILLS IN (Ahmed, 28 Oct 2026): the group's own row replaces the parent row in
 *  this same slot; the way back is the top bar's parent crumb (Billing › Master Data), not an item in the row
 *  (see `billing/components/master-data-tabs.tsx`).
 *  This reverses the 13 Oct Antonin choice of a `SubTabsMenu` dropdown after the tabs ("one compact
 *  header"); SubTabsMenu was removed with its last users. The header still carries one tab row. */
export const PAGE_TABS_BAR = 'flex h-full items-stretch gap-6';
export const pageTabClass = (on: boolean) =>
  cn(
    'page-tab relative inline-flex h-full shrink-0 cursor-pointer items-end gap-2 pb-[var(--page-header-pad)] whitespace-nowrap text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full',
    on
      ? 'page-tab-on text-[var(--ink-body)] after:bg-[var(--accent-primary)]'
      : 'text-[var(--ink-secondary)] hover:text-[var(--ink-body)]',
  );

export function PageTabs<Id extends string = string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  className,
  tour,
}: {
  tabs: ReadonlyArray<PageTab<Id>>;
  active: Id;
  onChange: (id: Id) => void;
  ariaLabel: string;
  className?: string;
  /** Release-tour anchor: lands on the tablist as `data-tour` (a tour step targets `[data-tour="…"]`). */
  tour?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} data-page-tabs data-tour={tour} className={cn(PAGE_TABS_BAR, className)}>
      {tabs.map((t) => {
        const on = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            data-page-tab={t.id}
            className={pageTabClass(on)}
          >
            {t.label}
            {t.count != null && t.count > 0 ? (
              <span
                className={cn(
                  'font-normal tabular-nums',
                  t.urgent ? 'text-[var(--status-bad)]' : 'text-[var(--ink-secondary)]',
                )}
              >
                {t.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Room kept for the More button. */
const MORE_W = 72;
/** The gap between tabs (gap-6). */
const TAB_GAP = 24;

/**
 * HeaderTabsFit — the header's tab slot. Keeps whatever tab set the page passes on one line and
 * moves the tabs that do not fit into a More menu; the active tab never goes. Measured on every
 * resize of the slot and whenever the tabs change. A More item clicks the real tab, so a link still
 * routes and a tab button still calls its page.
 */
export function HeaderTabsFit({ children }: { children: ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState<{ label: string; el: HTMLElement }[]>([]);
  const [open, setOpen] = useState(false);
  const [minW, setMinW] = useState(0);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const box = boxRef.current;
    if (!wrap || !box) return;
    let frame = 0;
    const fit = () => {
      const tabs = [...box.querySelectorAll<HTMLElement>('.page-tab')];
      tabs.forEach((t) => {
        t.style.display = '';
      });
      const widths = tabs.map((t) => t.offsetWidth + TAB_GAP);
      const room = wrap.clientWidth;
      // When every tab fits, the row keeps --page-header-tabs-end free after its last tab (Antonin, 14 Oct);
      // once tabs go into More, that room goes back to the tabs.
      const end = parseFloat(getComputedStyle(wrap).getPropertyValue('--page-header-tabs-end')) || 0;
      // Not laid out yet (first pass, a hidden panel, jsdom): hide nothing.
      if (room === 0) {
        setMore((m) => (m.length ? [] : m));
        return;
      }
      const total = widths.reduce((a, w) => a + w, 0) + end;
      const ai = tabs.findIndex((t) => t.classList.contains('page-tab-on'));
      const min = ai >= 0 ? widths[ai] + (tabs.length > 1 ? MORE_W : 0) : 0;
      setMinW((m) => (m === min ? m : min));
      if (total <= room) {
        setMore((m) => (m.length ? [] : m));
        return;
      }
      let used = MORE_W + (ai >= 0 ? widths[ai] : 0);
      const hidden: HTMLElement[] = [];
      tabs.forEach((t, i) => {
        if (i === ai) return;
        if (hidden.length === 0 && used + widths[i] <= room) used += widths[i];
        else hidden.push(t);
      });
      hidden.forEach((t) => {
        t.style.display = 'none';
      });
      setMore(hidden.map((el) => ({ label: el.textContent ?? '', el })));
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    ro?.observe(wrap);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
    mo?.observe(box, { subtree: true, childList: true, characterData: true, attributeFilter: ['class'] });
    document.fonts?.ready?.then(schedule);
    return () => {
      cancelAnimationFrame(frame);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    // Stretched to the header's full height, so every underline lands on the header's hairline.
    // minWidth is measured at runtime (the active tab + More), not a design value.
    <div
      ref={wrapRef}
      data-header-tabs
      className="relative flex min-w-0 flex-1 basis-0 items-stretch justify-end gap-6 self-stretch"
      style={{ minWidth: minW }}
    >
      <div ref={boxRef} className="flex min-w-0 items-stretch justify-end [&>*]:h-full" style={{ marginRight: more.length ? 0 : 'var(--page-header-tabs-end)' }}>
        {children}
      </div>
      {more.length > 0 ? (
        <div className="relative flex shrink-0 items-stretch">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-header-tabs-more
            className={cn(pageTabClass(false), 'page-tab-more')}
          >
            More <ChevronDown className="size-4" aria-hidden />
          </button>
          {open ? (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+4px)] z-50 min-w-[var(--menu-min-width)] rounded-input border border-[var(--surface-line)] bg-[var(--surface-read)] p-1 shadow-overlay"
            >
              {more.map((m) => (
                <button
                  key={m.label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    m.el.click();
                  }}
                  className="block w-full whitespace-nowrap rounded-input px-3 py-2 text-left text-sm text-[var(--ink-body)] hover:bg-[var(--surface-panel)]"
                >
                  {m.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
